import type { UserProfile } from "../typings";
import { AppEntrypoint, MSO } from "../typings/enums";

export type MigrationEntryPoint =
  | "provider_chat"
  | "side_effects"
  | "refill_questionnaire"
  | "payment_method"
  | "additional_setup"
  | "appointment"
  | "lab_work"
  | "medication_order"
  | "dashboard_cta";

export type SmdMigrationAccessStatus =
  | "loading"
  | "unavailable"
  | "allowed"
  | "contact_required";

export const MIGRATION_ENTRY_POINTS: MigrationEntryPoint[] = [
  "provider_chat",
  "side_effects",
  "refill_questionnaire",
  "payment_method",
  "additional_setup",
  "appointment",
  "lab_work",
  "medication_order",
  "dashboard_cta",
];

export const isMigrationEntryPoint = (
  value: string
): value is MigrationEntryPoint =>
  MIGRATION_ENTRY_POINTS.includes(value as MigrationEntryPoint);

/**
 * Dashboard CTA audit.
 *
 * Every CTA reachable from an Ionic dashboard card is classified here so the
 * routing decision lives in one reviewable place instead of being rediscovered
 * per card. `null` marks the exceptions: read-only surfaces that non-migrated
 * users must keep reaching (their treatment plan, the clinician chat, and any
 * "view details" screen).
 *
 * Keys are lowercased CTA labels. Unknown labels intercept by default so a CTA
 * added later cannot silently leak a non-migrated user into a SteadyMD flow.
 */
export const DASHBOARD_CTA_AUDIT: Record<string, MigrationEntryPoint | null> = {
  // --- Exceptions: read-only, keep today's behavior -----------------------
  view: null, // TreatmentPlanCard -> View Treatment Plan
  "view details": null, // MedicationOrderCard / AppointmentCard / PriorAuthCard
  "view message": null, // TreatmentPlanCard -> clinician chat
  "chat with provider": null, // MedicationOrderCard -> clinician chat
  "medication instructions": null, // TreatmentPlanCard details row
  "prescription details": null, // TreatmentPlanCard details row
  "side effects information": null, // TreatmentPlanCard details row
  "after-visit summary": null, // PrescriptionUpdateModal row
  review: null, // PrescriptionUpdateModal -> treatment plan care update

  // --- Intercepted: actions that start or change SteadyMD care -----------
  "review payment method": "payment_method",
  "complete setup": "additional_setup",
  "start check-in": "refill_questionnaire",
  "continue check-in": "refill_questionnaire",
  "start lab work": "lab_work",
  "go to amazon": "medication_order",
  "track order": "medication_order",
  "confirm delivery": "medication_order",
  "report an issue": "medication_order",
  "schedule appointment": "appointment",
  "reschedule appointment": "appointment",
  reschedule: "appointment",
  "edit appointment": "appointment",
  "cancel appointment": "appointment",
  confirm: "appointment",
  "join now": "appointment",
  zoom: "appointment",
  // Looks client-side, but generateAndDownloadICS finishes by calling
  // updateConsult(guid, true), which writes confirmed: true to the SteadyMD
  // consult. Adding the event to a calendar also confirms the appointment.
  "add to calendar": "appointment",
};

/**
 * Resolves the migration entry point for a dashboard CTA. Returns `null` for
 * audited exceptions and falls back to a generic entry point for unaudited
 * labels so interception fails closed.
 */
export const getDashboardCtaEntryPoint = (
  label: string
): MigrationEntryPoint | null => {
  const key = label.trim().toLowerCase();

  // Own-property lookup only: a label such as "constructor" or "toString"
  // would otherwise resolve to an inherited Object.prototype member and build
  // an invalid /migration-support route.
  return Object.prototype.hasOwnProperty.call(DASHBOARD_CTA_AUDIT, key)
    ? DASHBOARD_CTA_AUDIT[key]
    : "dashboard_cta";
};

export const getSmdMigrationAccessStatus = ({
  profile,
  interceptionEnabled,
}: {
  profile?: Partial<UserProfile> | null;
  interceptionEnabled: boolean;
}): Exclude<SmdMigrationAccessStatus, "loading"> => {
  if (!interceptionEnabled) return "allowed";

  if (!profile?.medicalProvider || !profile.app_entrypoint) {
    return "unavailable";
  }

  return profile.medicalProvider === MSO.steadymd &&
    profile.app_entrypoint === AppEntrypoint.fh_plus_web
    ? "contact_required"
    : "allowed";
};
